import { CrossFilter, DrillLevel } from '../../services/dashboard-state.service';

export function formatDateIso(date: Date): string {
  if (!date || isNaN(date.getTime())) {
    return '';
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function intersectDateRanges(
  baseStart: string | null,
  baseEnd: string | null,
  focusStart: string | null,
  focusEnd: string | null
): { startDate: string | null; endDate: string | null } {
  const startCandidates = [baseStart, focusStart].filter((x): x is string => !!x);
  const endCandidates = [baseEnd, focusEnd].filter((x): x is string => !!x);
  const nextStart = startCandidates.length ? startCandidates.sort()[startCandidates.length - 1] : null;
  const nextEnd = endCandidates.length ? endCandidates.sort()[0] : null;
  if (nextStart && nextEnd && nextStart > nextEnd) {
    return { startDate: nextStart, endDate: nextEnd };
  }
  return { startDate: nextStart, endDate: nextEnd };
}

export function rangeForPeriodClick(period: string, level: DrillLevel): Partial<CrossFilter> {
  if (!period) return {};

  // 1. Detect if period is a full date (yyyy-MM-dd)
  if (/^\d{4}-\d{2}-\d{2}$/.test(period)) {
    if (level === 'week') {
      const start = new Date(period);
      if (isNaN(start.getTime())) {
        return {};
      }
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return {
        week: period,
        startDate: formatDateIso(start),
        endDate: formatDateIso(end)
      };
    } else {
      return {
        day: period,
        startDate: period,
        endDate: period
      };
    }
  }

  // 2. Detect if period is a quarter (yyyy-Qn)
  if (/^\d{4}-Q[1-4]$/.test(period)) {
    const [yearStr, qStr] = period.split('-Q');
    const year = Number(yearStr);
    const quarter = Number(qStr);
    if (isNaN(year) || isNaN(quarter)) {
      return {};
    }
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    const lastDay = new Date(year, endMonth, 0).getDate();
    return {
      quarter: period,
      startDate: `${year}-${String(startMonth).padStart(2, '0')}-01`,
      endDate: `${year}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
    };
  }

  // 3. Detect if period is a month (yyyy-MM)
  if (/^\d{4}-\d{2}$/.test(period)) {
    const [year, month] = period.split('-').map(Number);
    if (isNaN(year) || isNaN(month)) {
      return {};
    }
    const lastDay = new Date(year, month, 0).getDate();
    return {
      month: period,
      startDate: `${period}-01`,
      endDate: `${period}-${String(lastDay).padStart(2, '0')}`
    };
  }

  // 4. Detect if period is a year (yyyy)
  if (/^\d{4}$/.test(period)) {
    const year = Number(period);
    if (isNaN(year)) {
      return {};
    }
    return {
      year: period,
      startDate: `${period}-01-01`,
      endDate: `${period}-12-31`
    };
  }

  // Fallback/Safety Check: Parse string as Date if it's none of the above
  const d = new Date(period);
  if (!isNaN(d.getTime())) {
    const iso = formatDateIso(d);
    return {
      day: iso,
      startDate: iso,
      endDate: iso
    };
  }

  return {};
}

export function effectiveDateRange(
  startDate: string | null,
  endDate: string | null,
  filter: CrossFilter | null
): { startDate: string | null; endDate: string | null } {
  if (filter?.day && /^\d{4}-\d{2}-\d{2}$/.test(filter.day)) {
    return intersectDateRanges(startDate, endDate, filter.day, filter.day);
  }
  if (filter?.week && /^\d{4}-\d{2}-\d{2}$/.test(filter.week)) {
    const start = new Date(filter.week);
    if (!isNaN(start.getTime())) {
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return intersectDateRanges(startDate, endDate, formatDateIso(start), formatDateIso(end));
    }
  }
  // month-level period
  if (filter?.month && /^\d{4}-\d{2}$/.test(filter.month)) {
    const [year, month] = filter.month.split('-').map(Number);
    if (!isNaN(year) && !isNaN(month)) {
      const lastDay = new Date(year, month, 0).getDate();
      if (!isNaN(lastDay)) {
        return intersectDateRanges(
          startDate,
          endDate,
          `${filter.month}-01`,
          `${filter.month}-${String(lastDay).padStart(2, '0')}`
        );
      }
    }
  }
  // quarter-level period: e.g. '2025-Q2'
  if (filter?.quarter && /^\d{4}-Q[1-4]$/.test(filter.quarter)) {
    const [y, qPart] = filter.quarter.split('-Q');
    const q = parseInt(qPart, 10);
    if (!isNaN(q)) {
      const startMonth = (q - 1) * 3 + 1;
      const endMonth = startMonth + 2;
      const lastDay = new Date(Number(y), endMonth, 0).getDate();
      if (!isNaN(lastDay)) {
        return intersectDateRanges(
          startDate,
          endDate,
          `${y}-${String(startMonth).padStart(2, '0')}-01`,
          `${y}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
        );
      }
    }
  }
  if (filter?.year && /^\d{4}$/.test(filter.year)) {
    return intersectDateRanges(startDate, endDate, `${filter.year}-01-01`, `${filter.year}-12-31`);
  }
  if (filter?.startDate || filter?.endDate) {
    const start = filter.startDate && /^\d{4}-\d{2}-\d{2}$/.test(filter.startDate) ? filter.startDate : startDate;
    const end = filter.endDate && /^\d{4}-\d{2}-\d{2}$/.test(filter.endDate) ? filter.endDate : endDate;
    return intersectDateRanges(startDate, endDate, start, end);
  }
  return { startDate, endDate };
}
